import * as _ from 'lodash';
import * as qs from 'query-string';
import * as moment from 'moment';
import { Component } from 'react';
import { Dispatch, connect } from 'react-redux';
import { IApplicationState, AppActionCreators, IAppState, InvoicingActionCreators, IInvoiceTransplantListResponse, IInvoiceTransplantRequest } from '@nkr/store/index';
import { IBaseResponse, IInvoiceTransplants } from '@nkr/models/index';
import { OrganType } from '@nkr/enums/index';
import { ColumnApi, CsvExportParams, GridApi, GridReadyEvent, ProcessCellForExportParams } from 'ag-grid-community';
import { toast } from 'react-toastify';
import { downloadFile } from '@nkr/services/download-file';
import { GridWrapper } from './styles';
import Grid from './grid';
import Filter from './filter';

interface IProps {
    isAdmin: boolean;
}

interface IStateProps {
    app: IAppState;
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>;
    hideLoading: () => void;
    getInvoiceList: (invoiceRequest: IInvoiceTransplantRequest) => Promise<IInvoiceTransplantListResponse>;
    downloadAll: (invoiceDate: string, invoiceNumbers: number[]) => Promise<any>;
    downloadAllPreviews: (invoiceDate: string, centers: { centerId: number, organType: number }[]) => Promise<any>;
    bulkInvoiceSend: (list: number[], invoiceRequest: IInvoiceTransplantRequest) => Promise<any>;
    generateInvoiceByCenter: (centerId: number, invoiceDate: string, organType: number, filteredOrganType?: number) => Promise<IInvoiceTransplantListResponse>;
    generateInvoicePreviewByCenter: (centerId: number, invoiceDate: string, organType: number, filteredOrganType?: number) => Promise<IInvoiceTransplantListResponse>;
    previewInvoice: (centerId: number, invoiceDate: string, organType: number) => Promise<any>;
    viewInvoice: (invoiceNumber: number) => Promise<any>;
    regenerateInvoice: (invoiceNumber: number, organType: number) => Promise<any>;
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    list: IInvoiceTransplants[];
    transplantStatusRequest: IInvoiceTransplantRequest;
    selectAllRows: boolean;
    isLoading: boolean;
}

class InvoiceContainer extends Component<TProps, IState> {
    readonly state: IState = {
        list: [],
        transplantStatusRequest: null,
        selectAllRows: false,
        isLoading: true,
    };

    gridApi: GridApi;
    columnApi: ColumnApi;

    onGridReady = (e: GridReadyEvent) => {
        this.gridApi = e.api;
        this.columnApi = e.columnApi;
        e.api.sizeColumnsToFit();
    };

    componentDidMount = () => {
        this.getData();
    };

    getData = () => {
        let parsed = qs.parse(location.search.length > 0 ? location.search : encodeURI(''));

        let transplantRequest: IInvoiceTransplantRequest = {
            invoiceYear: parsed.invoiceYear != null ? parsed.invoiceYear : moment().add(-1, 'months').year().toString(),
            invoiceMonth: parsed.invoiceMonth != null ? parsed.invoiceMonth : (moment().add(-1, 'months').month() + 1).toString(),
            transplantType: parsed.transplantType != null ? parsed.transplantType : 1,
            organType: parsed.organType != null ? parsed.organType : null
        };

        this.props.showLoading('Getting list of invoices, one moment please...').then(async () => {
            let response = await this.props.getInvoiceList(transplantRequest);

            if (response.data.success) {
                let list = response.data.results;
                let isLoading = list.length === 0 ? false : true;

                this.setState({
                    list,
                    isLoading,
                }, () => {
                    // Refactor this
                    this.props.hideLoading();
                });
            }
        });
    };

    handleSearch = (data: IInvoiceTransplantRequest) => {
        this.props.showLoading('Searching, one moment please...').then(() => {
            this.props.getInvoiceList(data).then((response) => {
                if (response.data.success) {
                    let results = response.data.results;

                    this.setState({
                        list: results,
                        transplantStatusRequest: data,
                    }, () => {
                        this.setConversionUrl(data);
                        this.props.hideLoading();
                    });
                } else {
                    this.props.hideLoading();
                }
            });
        });
    };

    handleLoadingComplete = () => {
        this.setState({
            isLoading: false,
        });
    };

    handleSelectAllRows = () => {
        let invoiceList = this.state.list.map((x) => x.id);

        let list = _.cloneDeep(this.state.list).map((x) => {
            if (invoiceList.indexOf(x.id) >= 0) {
                x.isSelected = !this.state.selectAllRows;
            }

            return x;
        });

        this.setState({
            list,
            selectAllRows: !this.state.selectAllRows,
        });
    };


    handleRowSelected = (id: number, isSelected: boolean, centerId?: number, organType?: number) => {
        let list = _.cloneDeep(this.state.list).map((x) => {
            let match = id != null
                ? x.id == id
                : x.centerId == centerId && x.organType == organType;

            if (match) {
                x.isSelected = isSelected;
            }

            return x;
        });

        this.setState({ list });
    };

    handleBulkInvoiceSend = () => {
        let parsed = qs.parse(location.search.length > 0 ? location.search : encodeURI(''));

        let transplantRequest: IInvoiceTransplantRequest = {
            invoiceYear: parsed.invoiceYear != null ? parsed.invoiceYear : moment().add(-1, 'months').year().toString(),
            invoiceMonth: parsed.invoiceMonth != null ? parsed.invoiceMonth : (moment().add(-1, 'months').month() + 1).toString(),
            transplantType: parsed.transplantType != null ? parsed.transplantType : 1,
            organType: parsed.organType != null ? parsed.organType : null
        };

        let selected = this.state.list.filter((item) => item.isSelected === true);

        let invoiceNumberList = selected
            .filter((item) => item.invoiceNumber != null && String(item.invoiceNumber).trim() !== '' && Number(item.invoiceNumber) > 0)
            .map((item) => item.invoiceNumber);

        if (invoiceNumberList.length === 0) {
            toast.info(selected.length === 0
                ? 'No invoices selected'
                : 'Selected row has no invoice number - generate the invoice first');
            return;
        }

        if (invoiceNumberList.length < selected.length) {
            toast.info(`Skipping ${selected.length - invoiceNumberList.length} selected row(s) without an invoice number`);
        }

        this.props.showLoading('Sending bulk invoices, one moment please...').then(() => {
            this.props.bulkInvoiceSend(invoiceNumberList, transplantRequest).then((response: any) => {
                if (response.data.success) {
                    let list = response.data.results;
                    let isLoading = list.length === 0 ? false : true;

                    this.setState({
                        list,
                        isLoading,
                    }, () => {
                        // Refactor this
                        toast.success('Invoices sent successfully');
                        this.props.hideLoading();
                        this.handleSearch(transplantRequest);
                    });
                }
            });
        });
    };

    previewInvoice = (centerId, invoiceDate, organType) => {
        let organLabel = organType != null ? organType == OrganType.Kidney ? "K" : "L" : "K";
        this.props.showLoading("Downloading Invoice Preview").then(async () => {
            await this.props.previewInvoice(centerId, invoiceDate, organType).then((response: any) => {
                downloadFile(response.data, `center-${centerId}-${organLabel}-preview-${invoiceDate}.pdf`, 'application/pdf');
            });

            this.props.hideLoading();
        });
    };

    viewInvoice = (invoiceNumber) => {
        this.props.showLoading("Downloading Invoice").then(() => {
            this.props.viewInvoice(invoiceNumber).then((response: any) => {
                downloadFile(response.data, `invoice-${invoiceNumber}.pdf`, 'application/pdf');

                this.props.hideLoading();
            })
        })
    };

    downloadAll = (invoiceDate) => {
        let invoiceNumberList = this.state.list
            .filter((item) => item.isSelected == true && item.invoiceNumber > 0)
            .map((item) => item.invoiceNumber);

        if (invoiceNumberList.length == 0) {
            toast.info('No generated invoices selected');
            return;
        }

        this.props.showLoading("Downloading Invoices").then(() => {
            this.props.downloadAll(invoiceDate, invoiceNumberList).then((response: any) => {
                downloadFile(response.data, `invoices-${invoiceDate}.zip`, 'application/zip');
                this.props.hideLoading();
            })
        })
    };

    downloadAllPreviews = (invoiceDate) => {
        let centerList = this.state.list
            .filter((item) => item.isSelected == true)
            .map((item) => ({ centerId: item.centerId, organType: item.organType }));

        if (centerList.length == 0) {
            toast.info('No centers selected');
            return;
        }

        this.props.showLoading("Downloading Invoice Previews").then(() => {
            this.props.downloadAllPreviews(invoiceDate, centerList).then((response: any) => {
                downloadFile(response.data, `previews-${invoiceDate}.zip`, 'application/zip');
                this.props.hideLoading();
            })
        })
    };

    regenerateInvoice = (invoiceNumber, organType) => {
        this.props.showLoading("Regenerating Invoice").then(() => {
            this.props.regenerateInvoice(invoiceNumber, organType).then((response: any) => {
                downloadFile(response.data, `invoice-${invoiceNumber}.pdf`, 'application/pdf');

                this.props.hideLoading();
            }).catch(async (error: any) => {
                this.props.hideLoading();
                let message = 'Failed to regenerate invoice.';
                const data = error?.response?.data;
                if (data instanceof Blob) {
                    const text = await data.text();
                    message = text
                }
                toast.error(message, { autoClose: false });
            });
        });
    };

    handleGenerateInvoicesByRow = (selectedKeys?: string[]) => {
        let lastDayPriorMonth = moment().clone().subtract(1, 'months').endOf('month');
        let invoiceDate = lastDayPriorMonth.format('YYYY-MM-DD');
        let parsed = qs.parse(location.search.length > 0 ? location.search : encodeURI(''));
        let filteredOrganType = parsed.organType != null ? parsed.organType : null;

        // capture selection on the first call only; recursive calls pass it back in
        let keys = Array.isArray(selectedKeys) ? selectedKeys : this.state.list
            .filter((item) => item.isSelected == true)
            .map((item) => `${item.centerId}-${item.organType}`);

        if (keys.length == 0) {
            toast.info('No centers selected');
            return;
        }

        let nextInvoice = this.state.list.find(
            (i) => i.id == null && keys.indexOf(`${i.centerId}-${i.organType}`) >= 0
        );

        if (typeof nextInvoice !== "undefined") {
            this.props.showLoading(`Generating ${OrganType[nextInvoice.organType]} invoice for ${nextInvoice.centerAlias}`).then(() => {
                this.props.generateInvoiceByCenter(nextInvoice.centerId, invoiceDate, nextInvoice.organType, filteredOrganType).then((response: any) => {
                    if (response.data.success) {
                        let list = response.data.results;
                        let isLoading = list.length !== 0;

                        this.setState({
                            list,
                            isLoading
                        }, () => {
                            toast.success('Invoice generated successfully');
                            this.props.hideLoading();
                            this.handleGenerateInvoicesByRow(keys);
                        })
                    } else {
                        toast.error(
                            <>
                                <div>{response.data.results}</div>
                            </>
                        );
                        this.props.hideLoading();
                    }
                })
            });
        }
    };

    handleGeneratePreviewsByRow = () => {
        let lastDayPriorMonth = moment().clone().subtract(1, 'months').endOf('month');
        let invoiceDate = lastDayPriorMonth.format('YYYY-MM-DD');
        let parsed = qs.parse(location.search.length > 0 ? location.search : encodeURI(''));
        let filteredOrganType = parsed.organType != null ? parsed.organType : null;

        // snapshot the selected rows up front - state.list is replaced on each response
        let selected = this.state.list
            .filter((item) => item.isSelected == true)
            .map((item) => ({
                centerId: item.centerId,
                organType: item.organType,
                centerAlias: item.centerAlias
            }));

        if (selected.length == 0) {
            toast.info('No centers selected');
            return;
        }

        let loop = async () => {
            for await (const nextInvoice of selected) {
                await this.props.showLoading(`Generating ${OrganType[nextInvoice.organType]} preview for ${nextInvoice.centerAlias}`).then(async () => {
                    await this.props.generateInvoicePreviewByCenter(nextInvoice.centerId, invoiceDate, nextInvoice.organType, filteredOrganType).then(async (response: any) => {
                        if (response.data.success) {
                            let list = response.data.results;
                            let isLoading = list.length !== 0;

                            await this.setState({
                                list,
                                isLoading
                            }, () => {
                                toast.success('Preview generated successfully');
                                this.props.hideLoading();
                            })
                        } else {
                            toast.error(
                                <>
                                    <div>{response.data.results}</div>
                                </>
                            );

                            this.props.hideLoading();
                        }
                    })
                });
            }
        }

        loop();
    };


    setConversionUrl = (data: IInvoiceTransplantRequest) => {
        let { invoiceMonth, invoiceYear, transplantType, organType } = data;
        let paramList = [];

        if (invoiceYear !== '') {
            paramList.push(encodeURI('invoiceYear=' + invoiceYear));
        }

        if (invoiceMonth !== '') {
            paramList.push(encodeURI('invoiceMonth=' + invoiceMonth));
        }

        if (transplantType !== null) {
            paramList.push(encodeURI('transplantType=' + transplantType));
        }
        if (organType !== null) {
            paramList.push(encodeURI('organType=' + organType));
        }

        let params = paramList.join('&');

        let newUrl = '/admin/invoice/index?' + params;

        window.history.pushState({}, '', newUrl);
    };

    csvExport = (month, year) => {
        let organType = this.state.transplantStatusRequest != null ? this.state.transplantStatusRequest.organType : null;

        let organLabel = organType == OrganType.Kidney ? 'Kidney' : organType == OrganType.Liver ? 'Liver' : 'All';

        let fileName = 'Invoices_' + organLabel + '_' + month + "_" + year + '.csv';

        let otherOrganColumns = organType == OrganType.Kidney
            ? ['transplantsFacilitatedL', 'transplantsNonFacilitatedL']
            : organType == OrganType.Liver
                ? ['transplantsFacilitatedK', 'transplantsNonFacilitatedK']
                : [];

        let columnKeys = this.columnApi.getAllColumns().filter(x => x.getColId() != 'actions' && x.getColId() != 'isSelected' && otherOrganColumns.indexOf(x.getColId()) == -1).map(x => x.getColId());

        let params: CsvExportParams = {
            columnGroups: true,
            allColumns: false,
            columnKeys: columnKeys,
            fileName: fileName,
            processCellCallback: (params: ProcessCellForExportParams) => {
                let colDef = params.column.getColDef();

                if (colDef.field == 'organType')
                    return params.node.rowPinned == 'bottom'
                        ? ''
                        : params.value == 0 ? 'Kidney' : params.value == 1 ? 'Liver' : params.value;

                if (params.node.rowPinned == 'bottom') {
                    if (colDef.colId == 'id')
                        return '';
                    if (colDef.colId == 'invoiceNumber')
                        return '';
                    if (colDef.colId == 'missingContact')
                        return '';
                    if (colDef.colId == 'missingFees')
                        return '';
                }

                if (colDef.colId == 'lastUpdated')
                    return params.value ? moment(params.value).format('MM/DD/YYYY') : '';
                if (colDef.colId == 'invoiceSent')
                    return params.value ? moment(params.value).format('MM/DD/YYYY') : '';
                if (colDef.colId == 'received')
                    return params.value ? moment(params.value).format('MM/DD/YYYY') : '';

                var result = params.value;
                return result;
            }
        };

        this.gridApi.exportDataAsCsv(params);
    }

    render = () => {
        return (
            <>
                {this.props.isAdmin && (
                    <>
                        <Filter
                            list={this.state.list}
                            filterRequest={this.state.transplantStatusRequest}
                            onSearch={this.handleSearch}
                            onSendInvoiceEmail={this.handleBulkInvoiceSend}
                            onGenerateInvoice={this.handleGenerateInvoicesByRow}
                            onGeneratePreview={this.handleGeneratePreviewsByRow}
                            onDownloadAll={this.downloadAll}
                            onDownloadAllPreviews={this.downloadAllPreviews}
                            csvExport={this.csvExport}
                        />

                        <div style={{ width: '100%' }}>
                            <Grid
                                list={this.state.list}
                                isLoading={this.state.isLoading}
                                onSelectAllRows={this.handleSelectAllRows}
                                onRowSelected={this.handleRowSelected}
                                onLoadingComplete={this.handleLoadingComplete}
                                onGridReady={this.onGridReady}
                                onPreviewClicked={this.previewInvoice}
                                onViewClicked={this.viewInvoice}
                                onRegenerateClicked={this.regenerateInvoice}
                                isAdmin={true}
                                isAdminLogin={true}
                            />

                            <div>{this.state.list.length} invoice(s) shown...</div>
                        </div>
                    </>
                )}
            </>
        );
    };
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            return dispatch(AppActionCreators.hideLoading());
        },
        getInvoiceList: (listType) => {
            return dispatch(InvoicingActionCreators.getInvoiceList(listType));
        },
        downloadAll: (invoiceDate: string, invoiceNumbers: number[]) => {
            return dispatch(InvoicingActionCreators.downloadAll(invoiceDate, invoiceNumbers));
        },
        downloadAllPreviews: (invoiceDate: string, centers: { centerId: number, organType: number }[]) => {
            return dispatch(InvoicingActionCreators.downloadAllPreviews(invoiceDate, centers));
        },
        bulkInvoiceSend: (list, listType) => {
            return dispatch(InvoicingActionCreators.bulkInvoiceSend(list, listType));
        },
        generateInvoiceByCenter: (centerId, invoiceDate, organType, filteredOrganType) => {
            return dispatch(InvoicingActionCreators.generateInvoiceByCenter(centerId, invoiceDate, organType, filteredOrganType));
        },
        generateInvoicePreviewByCenter: (centerId, invoiceDate, organType, filteredOrganType) => {
            return dispatch(InvoicingActionCreators.generateInvoicePreviewByCenter(centerId, invoiceDate, organType, filteredOrganType));
        },
        previewInvoice: (centerId, invoiceDate, organType) => {
            return dispatch(InvoicingActionCreators.previewInvoice(centerId, invoiceDate, organType));
        },
        viewInvoice: (invoiceNumber) => {
            return dispatch(InvoicingActionCreators.viewInvoice(invoiceNumber));
        },
        regenerateInvoice: (invoiceId, organType) => {
            return dispatch(InvoicingActionCreators.regenerateInvoice(invoiceId, organType));
        },

    };
};

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(InvoiceContainer);